import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, filter, takeUntil } from 'rxjs';
import { DataService } from 'src/app/services/data.service';
import { OrderService } from 'src/app/services/order.service';
import { LocalizationService } from 'src/app/services/localization.service';
import { DrinkOrder } from 'src/interfaces/drink.model';
import { FoodOrder } from 'src/interfaces/food.model';
import { Locale } from 'src/interfaces/locale.model';
import { Order } from 'src/interfaces/order.model';
import { Restaurant } from 'src/interfaces/restaurant.model';
import { SheetDirective } from 'src/app/directives/sheet.directive';

@Component({
    selector: 'app-menu-order-dialog',
    templateUrl: './menu-order-dialog.component.html',
    styleUrls: ['./menu-order-dialog.component.css'],
    standalone: false
})
export class MenuOrderDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private dialogRef: MatDialogRef<MenuOrderDialogComponent>,
    private dataService: DataService,
    private orderService: OrderService,
    private localization: LocalizationService,
  ) {
    this.locale = this.dataService.locale;
    this.currentGuestId = this.dataService.guestId;
    this.restaurant = this.dataService.restaurant;

    // Затемнение и Esc закрывают окно с анимацией, а не мгновенно
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => this.close());
    this.dialogRef.keydownEvents()
      .pipe(filter((event) => event.key === 'Escape'))
      .subscribe(() => this.close());
  }

  @ViewChild('scrollContent') scrollContent!: ElementRef;
  @ViewChild(SheetDirective) sheet!: SheetDirective;

  order: Order | null = null;
  locale: Locale | null;
  foods: FoodOrder[] | null = null;
  drinks: DrinkOrder[] | null = null;
  restaurant: Restaurant | null;
  otherGuestsWithItems: { guestId: string; foods: any[]; drinks: any[] }[] = [];
  private currentGuestId: string;
  private destroy$ = new Subject<void>();

  /** С чем закрыть диалог — после того, как окно уедет вниз */
  private payment = false;

  ngOnInit(): void {
    this.orderService.subscribeToOrder()
      .pipe(takeUntil(this.destroy$))
      .subscribe(order => {
        if (order) {
          this.order = order;
          this.foods = order.guests?.[this.currentGuestId]?.foods;
          this.drinks = order.guests?.[this.currentGuestId]?.drinks;

          this.otherGuestsWithItems = Object.entries(this.order.guests)
            .filter(([id]) => id !== this.currentGuestId)
            .map(([id, guest]) => ({
              guestId: id,
              foods: guest.foods ?? [],
              drinks: guest.drinks ?? [],
            }))
            .filter(guest => guest.foods.length > 0 || guest.drinks.length > 0);

        }
      });
  }

  /** Свои непринятые позиции — их можно отменить */
  get canCancel(): boolean {
    return this.orderService.hasOwnNewItems(this.order);
  }

  /** Заказ ещё не приняли — отменяется целиком, иначе — новые позиции */
  get cancelsWholeOrder(): boolean {
    return this.order?.status === 'Created';
  }

  async cancel(): Promise<void> {
    const question = this.localization.translate(
      this.cancelsWholeOrder ? 'cancelOrderConfirm' : 'cancelNewItemsConfirm');
    if (!window.confirm(question)) return;
    try {
      await this.orderService.cancelOwnNewItems();
    } catch (error) {
      console.error('MenuOrderDialog | cancel() |', error);
    }
  }

  ngAfterViewInit() {
    this.scrollContent.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
  }

  onScroll(event: any) {
    const scrollOffset = this.scrollContent.nativeElement.scrollTop;
  }

  ngOnDestroy(): void {
    this.scrollContent.nativeElement.removeEventListener('scroll', this.onScroll.bind(this));
    // Очистка подписки
    this.destroy$.next();
    this.destroy$.complete();
    // console.log('MenuOrderDialog | ngOnDestroy() | subscription cancelled');
  }

  /** Окно уезжает вниз, затем диалог закрывается (onSheetClosed) */
  close(payment: boolean = false): void {
    this.payment = payment;
    this.sheet.dismiss();
  }

  onSheetClosed(): void {
    this.dialogRef.close({ payment: this.payment });
  }

  getTotalOwner(): number {
    const foodTotal = this.foods?.filter(f => f.status !== 'Cancelled').reduce((sum, f) => sum + (f.price || 0) * (f.count || 1), 0) || 0;
    const drinkTotal = this.drinks?.filter(d => d.status !== 'Cancelled').reduce((sum, d) => sum + (d.price || 0) * (d.count || 1), 0) || 0;
    return foodTotal + drinkTotal;
  }

  getTotalGuest(guest: { foods: { price: number, count: number, status?: string }[]; drinks: { price: number,  count: number, status?: string }[] }): number {
    const foodTotal = guest.foods?.filter(f => f.status !== 'Cancelled').reduce((sum, f) => sum + (f.price || 0) * (f.count || 1), 0) || 0;
    const drinkTotal = guest.drinks?.filter(d => d.status !== 'Cancelled').reduce((sum, d) => sum + (d.price || 0) * (d.count || 1), 0)  || 0;
    return foodTotal + drinkTotal;
  }

  getTotal(): number {
    let total = this.getTotalOwner();
    this.otherGuestsWithItems.forEach(guest => {
      total += this.getTotalGuest(guest);
    });
    return total;
  }

}
